"use client";

/**
 * Auth context — holds current user state, provides login/logout actions.
 *
 * Security:
 * - No tokens stored in JS state. Auth tokens are in HTTPOnly cookies.
 * - This context tracks the *user identity* returned by the /me endpoint.
 * - On page reload, identity is re-fetched via refresh + /me.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { needsTotpSetup } from "@/lib/auth/needs-totp-setup";
import { useRouter } from "next/navigation";
import type {
  LoginResponse,
  MeResponse,
  MeResponseCompany,
} from "@/lib/api/schemas";
import { getMe, logout as apiLogout } from "@/lib/api/auth";
import { APIClientError, attemptRefresh } from "@/lib/api/client";
import { onAuthEvent, postAuthEvent } from "./broadcast";

// ── Types ────────────────────────────────────────────────

interface AuthUser {
  userId: string;
  companyId: string | null;
  email: string;
  fullName: string;
  status: string;
  isGlobalAdmin: boolean;
  isCompanyAdmin: boolean;
  /** Effective product modules this user can access, already resolved
   *  server-side (GA → all modules, CA → all company modules, regular
   *  user → intersection of company + user grants). Components use
   *  ``hasModule(user, ModuleType.X)`` (see useEnabledModules) to gate
   *  navigation; never inspect this list directly to keep module-naming
   *  out of arbitrary call sites. */
  enabledModules: string[];
  mustChangePassword: boolean;
  mfaEnabled: boolean;
  /** Personal IANA timezone preference. Null = inherit (company TZ for
   *  company users, Europe/Berlin for global admins). */
  timezone: string | null;
  /** Company-level metadata returned by /auth/me — populated for
   *  company users (CA + module users), null for global admins.
   *  Consumed by ``ObservabilityBridge`` for PostHog Group Properties;
   *  see ``plans/PLAN_POSTHOG_01_FOUNDATIONS.md`` § "Group Properties". */
  company: MeResponseCompany | null;
}

interface AuthContextValue {
  user: AuthUser | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  mfaSetupRequired: boolean;
  setUserFromLogin: (data: LoginResponse) => void;
  setUserFromMe: (data: MeResponse) => void;
  logout: () => Promise<void>;
  clearUser: () => void;
}

// ── Context ──────────────────────────────────────────────

const AuthContext = createContext<AuthContextValue | null>(null);

// ── Helpers ──────────────────────────────────────────────

function meToAuthUser(data: MeResponse): AuthUser {
  return {
    userId: data.user_id,
    companyId: data.company_id,
    email: data.email,
    fullName: data.full_name,
    status: data.status,
    isGlobalAdmin: data.is_global_admin,
    isCompanyAdmin: data.is_company_admin,
    enabledModules: data.enabled_modules,
    mustChangePassword: data.must_change_password,
    mfaEnabled: data.mfa_enabled,
    timezone: data.timezone ?? null,
    company: data.company ?? null,
  };
}

// ── Provider ─────────────────────────────────────────────

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [mfaSetupRequired, setMfaSetupRequired] = useState(false);
  const router = useRouter();

  // On mount, attempt to restore session via refresh + /me
  useEffect(() => {
    async function restoreSession() {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 15_000);
      try {
        // Ueber attemptRefresh, nicht ueber ein eigenes fetch. Das Schloss
        // dahinter (Web Locks, plus ein Versionszaehler in localStorage) ist
        // der einzige Grund, aus dem zwei gleichzeitige Aktualisierungen sich
        // nicht gegenseitig entwerten - und seit die Seite ihre Daten schon
        // waehrend dieser Pruefung laedt, ist "gleichzeitig" der Normalfall
        // und nicht mehr der Sonderfall. extractCSRFToken macht doRefresh
        // selbst, deshalb faellt es hier weg.
        const outcome = await attemptRefresh();

        if (!outcome.ok) {
          setUser(null);
          return;
        }

        // Refresh succeeded — fetch full user profile
        const me = await getMe();
        setUser(meToAuthUser(me));

        // The SERVER decides. It knows the environment, and local dev can
        // switch mandatory admin MFA off (MI_DEV_DISABLE_ADMIN_MFA) — which
        // used to have no effect at all, because the client re-derived the
        // rule and bounced the admin to /mfa/setup even as the API waved them
        // through. Fall back to deriving it only when an older backend does
        // not send the field.
        // Regular users: MFA is enforced on new devices at login, not forced
        // to set up. Skip while a password change is pending — that takes
        // priority and will force re-login.
        if (needsTotpSetup(me) && !me.must_change_password) {
          setMfaSetupRequired(true);
        }
      } catch (err) {
        if (err instanceof APIClientError && err.code === "mfa_setup_required") {
          // Session is valid but MFA setup is enforced — keep user as authenticated
          // so ProtectedRoute can redirect to /mfa/setup instead of /login
          setMfaSetupRequired(true);
        } else {
          setUser(null);
        }
      } finally {
        clearTimeout(timeoutId);
        setIsLoading(false);
      }
    }

    restoreSession();
  }, []);

  const setUserFromLogin = useCallback((data: LoginResponse) => {
    // Set user immediately from login response so isAuthenticated = true
    // BEFORE navigation happens. This prevents ProtectedRoute from
    // seeing user=null and redirecting back to /login.
    const loginUser: AuthUser = {
      userId: data.user_id,
      companyId: data.company_id,
      email: "",
      fullName: "",
      status: data.status,
      isGlobalAdmin: data.is_global_admin,
      isCompanyAdmin: data.is_company_admin,
      // Empty until the background getMe() resolves. Module-gated nav
      // briefly renders nothing rather than the wrong nav; the gap is
      // <100ms on a warm cache.
      enabledModules: [],
      mustChangePassword: data.must_change_password,
      mfaEnabled: data.mfa_enabled,
      timezone: null, // populated by the background getMe() below
      // company hydrates via the background getMe(); until then the
      // bridge attaches the company group with id only and re-runs
      // identify+group when the full object lands.
      company: null,
    };
    setUser(loginUser);
    setIsLoading(false);

    // Per requirement: the timezone-mismatch banner reappears on every
    // fresh login. Clearing the per-user dismissal key resets it
    // regardless of whether sessionStorage was already empty.
    if (typeof window !== "undefined") {
      try {
        sessionStorage.removeItem(`tz-mismatch-dismissed:${data.user_id}`);
      } catch {
        // sessionStorage might be disabled — banner just shows on next render.
      }
    }

    // Global admins and company admins without MFA must set it up first.
    // Also flag when TOTP is missing (WebAuthn-only users need a TOTP fallback).
    // Skip if password change is pending — that takes priority and will force re-login.
    // Same rule as the restore path above: the server's answer wins when it
    // gives one. `must_setup_totp` also covers WebAuthn-only users, who need
    // a TOTP fallback even though they are not admins.
    if (!data.must_change_password && needsTotpSetup(data)) {
      setMfaSetupRequired(true);
    }

    // Upgrade with full profile in background (non-blocking)
    getMe()
      .then((me) => {
        setUser(meToAuthUser(me));
        // The SERVER owns the MFA-setup decision — it knows the environment
        // and whether the local-dev bypass (MI_DEV_DISABLE_ADMIN_MFA) is on.
        // Use needsTotpSetup(me) so ``must_setup_totp`` from /me wins, exactly
        // like the restore path above and the login-response check. Re-deriving
        // the rule inline here ignored the bypass and bounced an admin to
        // /mfa/setup even as the API waved them through.
        if (needsTotpSetup(me) && !me.must_change_password) {
          setMfaSetupRequired(true);
        }
      })
      .catch((err) => {
        if (err instanceof APIClientError && err.code === "mfa_setup_required") {
          setMfaSetupRequired(true);
        }
        // Keep the login-derived user — good enough for routing
      });
  }, []);

  const setUserFromMe = useCallback((data: MeResponse) => {
    setUser(meToAuthUser(data));
    setIsLoading(false);
    // Clear MFA setup requirement if MFA is now enabled
    if (data.mfa_enabled) {
      setMfaSetupRequired(false);
    }
  }, []);

  const logout = useCallback(async () => {
    // Wait for the backend to invalidate the session cookies BEFORE we
    // touch local state or navigate. If we soft-navigated to /login
    // while the ``access_token`` cookie was still valid, the Next.js
    // middleware (proxy.ts) would see ``hasSession=true`` on a guest-
    // only path and redirect us to /dashboard — landing the user on a
    // protected route with ``user=null``, i.e. the ProtectedRoute
    // spinner with no way out other than F5.
    await apiLogout().catch(() => {
      // apiLogout already swallows its own errors; this is just defence.
    });

    // Broadcast AFTER cookies are gone so sibling tabs that react by
    // navigating to /login don't hit the same middleware bounce.
    postAuthEvent({ type: "logout" });

    setUser(null);
    setMfaSetupRequired(false);

    // Hard navigation: middleware re-evaluates with cleared cookies AND
    // every React Query / module state attached to the old session is
    // dropped. Avoids the class of bug where a stale query rehydrates
    // a logged-out session before the navigation commits.
    window.location.replace("/login");
  }, []);

  const clearUser = useCallback(() => {
    setUser(null);
  }, []);

  // Listen for auth events from other tabs (logout, password change)
  useEffect(() => {
    return onAuthEvent((event) => {
      if (event.type === "logout" || event.type === "session_expired") {
        setUser(null);
        setMfaSetupRequired(false);
        router.replace("/login");
      } else if (event.type === "password_changed") {
        setUser(null);
        setMfaSetupRequired(false);
        router.replace("/login");
      }
    });
  }, [router]);

  // Server-side session heartbeat.
  //
  // Why: when another device takes over this user's session (force-login
  // from a different browser / incognito tab), the server revokes the
  // access-token JTI and deactivates the session row. This tab only
  // *notices* on its next authenticated request. If the user is idle on
  // /dashboard — no polling queries, no interaction — they sit there
  // indefinitely looking at a stale screen while their session is dead.
  //
  // BroadcastChannel can cover same-browser multi-tab, but it does NOT
  // cross the normal/private-window boundary, which is exactly the
  // scenario the user reported. So we ping /auth/me from the server side
  // too: every 60s while visible, and immediately when the tab regains
  // focus. The 401 handler in client.ts then redirects to /login with a
  // reason query param.
  useEffect(() => {
    if (user === null) return;

    let cancelled = false;

    async function heartbeat() {
      if (cancelled || document.visibilityState !== "visible") return;
      try {
        await getMe();
      } catch {
        // getMe's failures propagate through the normal client.ts 401 path
        // (which redirects to /login). Anything else here is transient —
        // swallow and try again next tick.
      }
    }

    const interval = window.setInterval(heartbeat, 60_000);
    const onVisible = () => {
      if (document.visibilityState === "visible") heartbeat();
    };
    document.addEventListener("visibilitychange", onVisible);

    // Check once immediately when we arrive at an authenticated screen.
    heartbeat();

    return () => {
      cancelled = true;
      window.clearInterval(interval);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [user]);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      isLoading,
      isAuthenticated: user !== null,
      mfaSetupRequired,
      setUserFromLogin,
      setUserFromMe,
      logout,
      clearUser,
    }),
    [user, isLoading, mfaSetupRequired, setUserFromLogin, setUserFromMe, logout, clearUser],
  );

  return <AuthContext value={value}>{children}</AuthContext>;
}

// ── Hook ─────────────────────────────────────────────────

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (ctx === null) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return ctx;
}
